import { format, isAfter, isBefore, isEqual, isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
  AxisLabelsFormatterContextObject,
  Point,
  Series,
  SeriesLineOptions,
  SeriesOptionsType,
  TooltipFormatterContextObject,
} from 'highcharts';
import { v4 } from 'uuid';

import {
  DEFAULT_SERIES_COLOR,
  DEFAULT_SERIES_FILL_COLOR,
  GAPS_SERIES_ID,
  SERIES_IN_COLLISION_COLOR,
  SERIES_IN_COLLISION_IN_PREVIEW_COLOR,
  SERIES_IN_PREVIEW_COLOR,
} from './config';
import { Adjustment, Scalars } from '../../graphql/components';
import { RecordBooleanType } from '../../types';

export const formatTooltipValue = (value: string | number, locale: string, precision?: number) =>
  Number(value).toLocaleString(locale, { minimumFractionDigits: precision, maximumFractionDigits: precision });

export function chartLegendLabelFormatter(this: Point | Series) {
  const {
    color,
    name,
    options: { id: seriesMappedKey },
    visible,
    /**
     * As we're rendering only Line type for all chart's series, this should never be of type Point. but the Highcharts type for Legend requires this typing...
     * The data contain also fields from SeriesOptionsType even though the exposed type doesn't match with it.
     * */
  } = this as Series & SeriesOptionsType;

  return `
    <div
      aria-checked="${visible}"
      class="composed-chart__legend-item"
      data-test-id="chart-legend-item:${seriesMappedKey}"
      role="checkbox"
    >
      <span class="composed-chart__legend-item-square">
        <span style="background-color: ${color};"></span>
      </span>
      <span class="composed-chart__legend-item-value">${name}</span>
    </div>
    `;
}

export function chartTooltipFormatter(
  label: string,
  dateFormat: string,
  lastXAxisPosition: number,
  timezone: string,
  locale: string,
  valuePrecision?: number,
  localeObj?: Locale
) {
  return function formatter(this: TooltipFormatterContextObject) {
    const points = this.points || [];
    const { x } = this;

    if (x === undefined) {
      return false;
    }

    const zonedDateTime = utcToZonedTime(x, timezone);

    // hide tooltip if last point selected (doesn't contain real value)
    if (zonedDateTime.getTime() === lastXAxisPosition) {
      return false;
    }

    /**
     * exclude all points that belong to gaps series or
     * represents the last value within base forecast series before occurrence of gap
     */
    const renderedPoints = points
      .map(
        ({
          color,
          // @ts-expect-error: incompatible type 😥
          point: { index },
          series: {
            data,
            name,
            // @ts-expect-error: incompatible type 😥
            options: { id, nameKey },
          },
          y,
        }) =>
          // @ts-expect-error: incompatible type 😥
          id?.match(GAPS_SERIES_ID) || (index !== data.length - 1 && data[index + 1].isNull)
            ? ''
            : `
        <tr data-test-id="highcharts-chart-tooltip-item:${nameKey}">
          <td>
            <span
              class="highcharts-chart__tooltip-item-indicator"
              style="background-color: ${color}"
            >
            </span>
            ${name}:
          </td>
          <td class="highcharts-chart__tooltip-value">${formatTooltipValue(y, locale, valuePrecision)}</td>
        </tr>
      `
      )
      .join('');

    // hide tooltip if no point should be rendered
    if (!renderedPoints) {
      return false;
    }

    return `
          <div class="highcharts-chart__tooltip">
            <table>
              <tbody>
                ${renderedPoints}
              </tbody>
            </table>
            <div class="highcharts-chart__tooltip-time">
              ${label}: <strong>${format(zonedDateTime, dateFormat, { locale: localeObj })}</strong>
            </div>
          </div>
          `;
  };
}

export function chartXAxisLabelFormatter(dateFormat: string, timezone: string, localeObj?: Locale) {
  return function formatter(this: AxisLabelsFormatterContextObject) {
    const { value } = this;
    const zonedDateTime = utcToZonedTime(value, timezone);

    return format(zonedDateTime, dateFormat, { locale: localeObj });
  };
}
export function chartYAxisLabelFormatter(locale: string, options?: Intl.NumberFormatOptions) {
  return function formatter(this: AxisLabelsFormatterContextObject) {
    const { value } = this;

    return value.toLocaleString(locale, options);
  };
}

export const generateChartGapSeriesOptions = (
  data: Scalars['ChartData'][],
  linkedSeriesId: string,
  id: string,
  color: string = DEFAULT_SERIES_COLOR
): SeriesOptionsType => ({
  color,
  data,
  id,
  connectNulls: false,
  enableMouseTracking: false,
  linkedTo: linkedSeriesId,
  marker: {
    enabled: false,
    lineWidth: 1,
    radius: 4,
    symbol: 'circle',
  },
  showInLegend: false,
  states: {
    hover: {
      enabled: false,
    },
    inactive: {
      opacity: 1,
    },
  },
  tooltip: {
    pointFormat: '',
  },
  type: 'line' as SeriesLineOptions['type'],
});

export const handleRangeCollisionsWithinAdjustments = (
  currentAdjustment: Adjustment,
  initialAdjustments: Adjustment[],
  adjustmentsInCollision: Record<string, boolean>
) => {
  const updatedAdjustments = initialAdjustments.reduce<Adjustment[]>(
    (adjustments, adjustment) => {
      const adjustmentStartDate = new Date(adjustment.dateTimeRange.startDateTime);
      const adjustmentEndDate = new Date(adjustment.dateTimeRange.endDateTime);
      const currentAdjustmentStartDate = new Date(currentAdjustment.dateTimeRange.startDateTime);
      const currentAdjustmentEndDate = new Date(currentAdjustment.dateTimeRange.endDateTime);

      // skip adding same adjustment when editing, as it would be added afterwards at the beginning of array
      if (adjustment.id !== currentAdjustment.id) {
        // skip check when no collision was pre-detected
        if (adjustmentsInCollision[adjustment.id]) {
          if (!isAfter(currentAdjustmentStartDate, adjustmentStartDate)) {
            if (!isBefore(currentAdjustmentEndDate, adjustmentEndDate)) {
              // new adjustment contains the existing one within its range, the existing one should be removed
              return adjustments;
            }

            // range of new adjustment overlaps with the existing one, need to update
            adjustments.push({
              ...adjustment,
              dateTimeRange: {
                startDateTime: currentAdjustment.dateTimeRange.endDateTime,
                endDateTime: adjustment.dateTimeRange.endDateTime,
              },
            });
          } else {
            if (!isBefore(currentAdjustmentEndDate, adjustmentEndDate)) {
              // new adjustment is placed at the end of the existing adjustment, need to crop from end
              adjustments.push({
                ...adjustment,
                dateTimeRange: {
                  startDateTime: adjustment.dateTimeRange.startDateTime,
                  endDateTime: currentAdjustment.dateTimeRange.startDateTime,
                },
              });
              return adjustments;
            }

            if (isEqual(currentAdjustmentStartDate, adjustmentStartDate)) {
              // new adjustment is placed at the beginning of the existing adjustment, need to crop from start
              adjustments.push({
                ...adjustment,
                dateTimeRange: {
                  startDateTime: currentAdjustment.dateTimeRange.endDateTime,
                  endDateTime: adjustment.dateTimeRange.endDateTime,
                },
              });
              return adjustments;
            }

            // new adjustment is the middle of existing one, need to split it into 2 ranges
            adjustments.push({
              ...adjustment,
              dateTimeRange: {
                startDateTime: adjustment.dateTimeRange.startDateTime,
                endDateTime: currentAdjustment.dateTimeRange.startDateTime,
              },
            });

            adjustments.push({
              ...adjustment,
              name: `${adjustment.name}-2`,
              id: v4(),
              dateTimeRange: {
                startDateTime: currentAdjustment.dateTimeRange.endDateTime,
                endDateTime: adjustment.dateTimeRange.endDateTime,
              },
            });
          }
        } else {
          adjustments.push(adjustment);
        }
      }

      return adjustments;
    },
    [currentAdjustment]
  );

  // adjustments should be sorted by dateTimeRange
  return updatedAdjustments.sort((a, b) =>
    isBefore(new Date(b.dateTimeRange.startDateTime), new Date(a.dateTimeRange.startDateTime)) ? 1 : -1
  );
};

export const getChartSeriesColor = (inCollision: boolean, inPreview: boolean) => {
  if (inCollision) {
    if (inPreview) {
      return SERIES_IN_COLLISION_IN_PREVIEW_COLOR;
    }

    return SERIES_IN_COLLISION_COLOR;
  }

  if (inPreview) {
    return SERIES_IN_PREVIEW_COLOR;
  }

  return DEFAULT_SERIES_FILL_COLOR;
};

export const getAdjustmentsInCollision = (
  adjustments: Adjustment[],
  rangeStartDate: string | Date,
  rangeEndDate: string | Date
) =>
  adjustments.reduce<RecordBooleanType>(
    (adjustmentsInCollision, { id, dateTimeRange: { startDateTime, endDateTime } }) => {
      if (
        isBefore(new Date(startDateTime), new Date(rangeStartDate)) ||
        isAfter(new Date(endDateTime), new Date(rangeEndDate))
      ) {
        // eslint-disable-next-line no-param-reassign
        adjustmentsInCollision[id] = true;
      }

      return adjustmentsInCollision;
    },
    {}
  );

export const amendAdjustmentsInCollision = (
  adjustments: Adjustment[],
  newStartDateTime: Date,
  newEndDateTime: Date,
  adjustmentsInCollision: RecordBooleanType
) =>
  adjustments.reduce<Adjustment[]>((newAdjustments, adjustment) => {
    const {
      dateTimeRange: { startDateTime: startDateTimeString, endDateTime: endDateTimeString },
    } = adjustment;

    const startDateTime = new Date(startDateTimeString);
    const endDateTime = new Date(endDateTimeString);

    // Checking if an adjustment is collision one and needs to be massaged
    if (adjustmentsInCollision[adjustment.id]) {
      if (
        isBefore(endDateTime, newStartDateTime) ||
        isSameDay(endDateTime, newStartDateTime) ||
        isAfter(startDateTime, newEndDateTime) ||
        isSameDay(startDateTime, newEndDateTime)
      ) {
        // Whole Adjustment is out of the new range, needs to be excluded!
      } else if (isBefore(startDateTime, newStartDateTime) || isSameDay(startDateTime, newStartDateTime)) {
        // Adjustment start is BEFORE or EQUAL new Range start
        // Checking position of Adjustment end
        if (isBefore(endDateTime, newEndDateTime) || isSameDay(endDateTime, newEndDateTime)) {
          /**
           * Adjustment end is BEFORE or EQUAL new Range end
           * Amending adjustment Start to be same as new Range start
           */
          newAdjustments.push({
            ...adjustment,
            dateTimeRange: {
              ...adjustment.dateTimeRange,
              startDateTime: newStartDateTime.toISOString(),
            },
          });
        } else if (isAfter(endDateTime, newEndDateTime)) {
          /**
           * Adjustment end is AFTER new Range end
           * Amending adjustment Start to be same as new Range start and End to be same as new Range end, both ends are cut
           */
          newAdjustments.push({
            ...adjustment,
            dateTimeRange: {
              startDateTime: newStartDateTime.toISOString(),
              endDateTime: newEndDateTime.toISOString(),
            },
          });
        }
      } else if (isBefore(startDateTime, newEndDateTime) && isAfter(endDateTime, newEndDateTime)) {
        /**
         * Adjustment start is BEFORE new Range end and Adjustment end is AFTER new Range end
         * Amending adjustment End to be same as new Range end
         */
        newAdjustments.push({
          ...adjustment,
          dateTimeRange: {
            ...adjustment.dateTimeRange,
            endDateTime: newEndDateTime.toISOString(),
          },
        });
      }
    } else {
      // Adjustment without collision
      newAdjustments.push(adjustment);
    }
    return newAdjustments;
  }, []);
